<script setup lang="ts">
import { computed, ref, toRef } from "vue";
import { useTranslation } from "i18next-vue";
import { useQueryClient } from "@tanstack/vue-query";
import APIClient from "@/APIClient";
import { QueryKey } from "@/stores/queries/query-keys";
import { useGetDrawingsByProject } from "@/stores/queries/drawings.query";
import { useToast } from "@/composable/useToast";
import type { DRDrawing, PaginationParams } from "@/types";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import DwgIcon from "@/components/UI/icons/DwgIcon.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

/**
 * Rattache un plan du projet à l'ouvrage (`Drawing.diagnosticId`). Pendant de
 * la modal « Lier un ouvrage » côté outil dessin, vue depuis l'ouvrage.
 */

const props = defineProps<{
  modelValue: boolean;
  diagnosticId: number;
  projectId: number;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "linked"): void;
}>();

const { t } = useTranslation();
const { showToast } = useToast();
const queryClient = useQueryClient();

const show = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const pagination = ref<PaginationParams>({
  skip: 0,
  take: 200,
  sortBy: "createdAt",
  order: "desc",
});
const search = ref("");

const { data, isLoading } = useGetDrawingsByProject(
  toRef(props, "projectId"),
  pagination,
  search
);

/** Les plans déjà rattachés à cet ouvrage sont retirés de la liste. */
const drawings = computed(() =>
  (data.value?.drawings ?? []).filter(
    (drawing) => drawing.diagnostic?.id !== props.diagnosticId
  )
);

const linkingId = ref<number | null>(null);

/** Un plan appartient à un seul ouvrage : celui déjà lié n'est pas cliquable. */
const link = async (drawing: DRDrawing) => {
  if (drawing.diagnostic || linkingId.value) return;
  linkingId.value = drawing.id;
  try {
    await APIClient.updateDrawing(drawing.id, {
      diagnosticId: props.diagnosticId,
    });
    queryClient.invalidateQueries({ queryKey: [QueryKey.DRAWINGS] });
    showToast("drawing.update.success");
    emit("linked");
    show.value = false;
  } catch {
    showToast("drawing.update.error", "warning");
  } finally {
    linkingId.value = null;
  }
};
</script>

<template>
  <DRDialog
    v-model="show"
    :title="t('report:diagnostics.linkPlanTitle')"
    hide-footer
  >
    <div class="mb-3 text-grey">
      {{ t("report:diagnostics.linkPlanDescription") }}
    </div>

    <el-input
      v-model="search"
      :placeholder="t('search')"
      clearable
      class="mb-3"
    />

    <div v-if="isLoading" class="text-center py-4">
      <span class="spinner-border spinner-border-sm" />
    </div>

    <div v-else-if="!drawings.length" class="text-center text-grey py-3">
      {{ t("report:diagnostics.linkPlanEmpty") }}
    </div>

    <ul v-else class="drawing-list">
      <li
        v-for="drawing in drawings"
        :key="drawing.id"
        class="drawing-row"
        :class="{ disabled: !!drawing.diagnostic }"
        :role="drawing.diagnostic ? undefined : 'button'"
        @click="link(drawing)"
      >
        <DwgIcon :size="28" class="flex-shrink-0" />
        <div class="drawing-info">
          <span class="drawing-name">{{ drawing.name }}</span>
          <span v-if="drawing.diagnostic" class="drawing-warning">
            {{
              t("report:diagnostics.linkPlanAlreadyLinked", {
                name:
                  drawing.diagnostic.name ||
                  t("report:diagnostics.item.noname"),
              })
            }}
          </span>
        </div>
        <span
          v-if="linkingId === drawing.id"
          class="spinner-border spinner-border-sm"
        />
        <PhosphorIcon
          v-else
          :name="drawing.diagnostic ? 'lock-simple' : 'link'"
          :size="18"
        />
      </li>
    </ul>
  </DRDialog>
</template>

<style lang="scss" scoped>
.drawing-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 320px;
  overflow-y: auto;
}

.drawing-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border: 1px solid var(--bs-border-color);
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    border-color: var(--bs-primary);
    background-color: var(--bs-tertiary-bg);
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background-color: var(--bs-secondary-bg);

    &:hover {
      border-color: var(--bs-border-color);
      background-color: var(--bs-secondary-bg);
    }
  }
}

.drawing-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex-grow: 1;
}

.drawing-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawing-warning {
  font-size: 12px;
  color: var(--bs-warning-text-emphasis, var(--bs-warning));
}
</style>
